import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { inferVideoProvider, normalizeMeetJoinUrl } from '../../utils/videoConsultLabel';

export type MeetDockRole = 'doctor' | 'staff';
export type MeetDockMode = 'mini' | 'full';

export type MeetDockSession = {
  visitUlid: string;
  role: MeetDockRole;
  joinUrl: string;
};

type StartMeetInput = {
  visitUlid: string;
  role: MeetDockRole;
  joinUrl: string;
};

type MeetSessionContextValue = {
  enabled: boolean;
  session: MeetDockSession | null;
  mode: MeetDockMode;
  joined: boolean;
  startMeet: (input: StartMeetInput) => boolean;
  setMode: (mode: MeetDockMode) => void;
  endMeet: () => void;
  markJoined: () => void;
  isThisVisit: (visitUlid: string) => boolean;
};

const STORAGE_KEY = 'aspen-meet-dock';

const MeetSessionContext = createContext<MeetSessionContextValue | null>(null);

function sameVisit(a: string, b: string): boolean {
  return a.replace(/-/g, '').toLowerCase() === b.replace(/-/g, '').toLowerCase();
}

function readStored(): { session: MeetDockSession; mode: MeetDockMode } | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw)
      return null;
    const parsed = JSON.parse(raw) as { session?: MeetDockSession; mode?: MeetDockMode };
    if (!parsed?.session?.visitUlid || !parsed.session.joinUrl)
      return null;
    if (parsed.session.role !== 'doctor' && parsed.session.role !== 'staff')
      return null;
    return {
      session: {
        visitUlid: parsed.session.visitUlid,
        role: parsed.session.role,
        joinUrl: normalizeMeetJoinUrl(parsed.session.joinUrl),
      },
      mode: parsed.mode === 'mini' ? 'mini' : 'full',
    };
  } catch {
    return null;
  }
}

function writeStored(session: MeetDockSession | null, mode: MeetDockMode): void {
  try {
    if (!session)
      sessionStorage.removeItem(STORAGE_KEY);
    else
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ session, mode }));
  } catch {
    /* private mode */
  }
}

export function MeetSessionProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}) {
  const stored = enabled ? readStored() : null;
  const [session, setSession] = useState<MeetDockSession | null>(stored?.session ?? null);
  const [mode, setModeState] = useState<MeetDockMode>(stored?.mode ?? 'full');
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setSession(null);
      setJoined(false);
      writeStored(null, 'full');
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled)
      return;
    writeStored(session, mode);
  }, [enabled, session, mode]);

  const isThisVisit = useCallback(
    (visitUlid: string) => Boolean(session && sameVisit(session.visitUlid, visitUlid)),
    [session],
  );

  const startMeet = useCallback((input: StartMeetInput): boolean => {
    if (!enabled || inferVideoProvider(input.joinUrl) !== 'Jitsi')
      return false;
    const joinUrl = normalizeMeetJoinUrl(input.joinUrl);
    if (session && !sameVisit(session.visitUlid, input.visitUlid)) {
      const ok = window.confirm('Leave the current video consult and join this visit instead?');
      if (!ok)
        return false;
    }
    if (session && sameVisit(session.visitUlid, input.visitUlid)) {
      setModeState('full');
      return true;
    }
    setJoined(false);
    setSession({ visitUlid: input.visitUlid, role: input.role, joinUrl });
    setModeState('full');
    return true;
  }, [enabled, session]);

  const setMode = useCallback((next: MeetDockMode) => {
    setModeState(next);
  }, []);

  const endMeet = useCallback(() => {
    setSession(null);
    setJoined(false);
    setModeState('full');
    writeStored(null, 'full');
  }, []);

  const markJoined = useCallback(() => setJoined(true), []);

  const value = useMemo(
    () => ({ enabled, session, mode, joined, startMeet, setMode, endMeet, markJoined, isThisVisit }),
    [enabled, session, mode, joined, startMeet, setMode, endMeet, markJoined, isThisVisit],
  );

  return (
    <MeetSessionContext.Provider value={value}>
      {children}
    </MeetSessionContext.Provider>
  );
}

export function useMeetSession(): MeetSessionContextValue {
  const ctx = useContext(MeetSessionContext);
  if (!ctx)
    throw new Error('useMeetSession must be used inside MeetSessionProvider');
  return ctx;
}

export function useOptionalMeetSession(): MeetSessionContextValue | null {
  return useContext(MeetSessionContext);
}
